import {
  moreTimeItems,
  pickActiveVariant,
  type EffortScale,
  type Exercise,
  type MoreTimeItem,
  type MoreTimeMinutes,
  type Program,
  type ProgramModel,
  type ProgramSession,
} from '@heraion/shared';

import type { MoreTimeRow } from './MoreTimeSheet';
import type { LoggerDraft } from './state/loggerDraftTypes';
import { rowIdOf, type DraftEntry } from './state/loggerDraftTypes';
import { inCalibration, type MoreTimeStep } from './state/moreTimeReducer';

// #356: the pure side of «More time». A slot's step lands on the row started
// from that slot (DraftEntry.planSlotId) still on the slot's exercise: a row
// swapped to another exercise, one added by hand, and every row of a draft
// from before the field match no slot. A step with nothing to land on is left
// out of the list and of the total, and spends none of the minutes (skipSlots).

export type MoreTimeSource = { program: Program; session: ProgramSession; weekIdx: number; model: ProgramModel };
export type Kept = { item: MoreTimeItem; step: MoreTimeStep | null; row: MoreTimeRow };

/** The draft row started from the program slot `slotId`, on its exercise. */
const entryOfSlot = (draft: LoggerDraft, slotId: string | undefined, exerciseId: string | undefined): DraftEntry | undefined =>
  slotId === undefined ? undefined : draft.entries.find((e) => e.planSlotId === slotId && e.exerciseId === exerciseId);

/** The slot's row in the draft, on the exercise its week prescribes. */
const entryOfSessionSlot = (src: MoreTimeSource, draft: LoggerDraft, slot: ProgramSession['slots'][number]): DraftEntry | undefined =>
  entryOfSlot(draft, slot.id, pickActiveVariant(slot, src.weekIdx)?.exerciseId);

/** The sets still to do on each slot that has its row in the draft. */
const setsToDoOf = (src: MoreTimeSource, draft: LoggerDraft): Map<string, number> =>
  new Map(
    src.session.slots.flatMap((s): Array<[string, number]> => {
      const e = entryOfSessionSlot(src, draft, s);
      return e ? [[s.id, e.sets.filter((x) => x.completedAt === null).length]] : [];
    }),
  );

/** The slots with no row to grow: none in the draft, or one in calibration.
 *  A superset slot also goes when its group is not in the draft (a lone member
 *  this round) or a member is in calibration: no round to add, no minutes spent. */
const skipSlotsOf = (src: MoreTimeSource, draft: LoggerDraft): Set<string> =>
  new Set(
    src.session.slots.flatMap((s) => {
      const e = entryOfSessionSlot(src, draft, s);
      if (!e || inCalibration(e)) return [s.id];
      if (!s.group) return [];
      const members = draft.entries.filter((m) => m.group?.id === s.group!.id);
      return members.length === 0 || members.some(inCalibration) ? [s.id] : [];
    }),
  );

export type KeepInputs = {
  effortScale: EffortScale;
  exercises: ReadonlyMap<string, Exercise>;
  profileRestSec: number | null | undefined;
};
export type KeepCtx = KeepInputs & {
  resolveName: (slug: string, snapshot?: string) => string;
  t: (k: string, opts?: Record<string, unknown>) => string;
};

/** The model's steps for `minutes`, each with its write and its line. */
export const keptFor = (source: MoreTimeSource, draft: LoggerDraft, minutes: MoreTimeMinutes, ctx: KeepCtx): Kept[] => {
  const { effortScale, exercises, profileRestSec, resolveName, t } = ctx;
  const setsToDo = setsToDoOf(source, draft);
  const skipSlots = skipSlotsOf(source, draft);
  const items = moreTimeItems({ ...source, minutes, effortScale, exercises, profileRestSec, setsToDo, skipSlots, inDraft: new Set(draft.entries.map((e) => e.exerciseId)) });
  return items.flatMap((item, i): Kept[] => {
    const name = resolveName(item.exerciseId);
    const key = `${i}:${item.rowId}`;
    if (item.add === 'row') {
      if (draft.entries.some((e) => e.exerciseId === item.exerciseId)) return [];
      const count = item.sets?.length ?? 0;
      return [{ item, step: null, row: { key, name, detail: t('mobile.logger.moreTime.addRow', { count, defaultValue: 'Comes back with {{count}} sets' }) } }];
    }
    if (item.groupId) {
      const members = draft.entries.filter((e) => e.group?.id === item.groupId);
      if (members.length === 0 || members.some(inCalibration)) return [];
      const names = members.map((e) => resolveName(e.exerciseId, e.exerciseName)).join(' + ');
      return [{ item, step: { add: 'round', groupId: item.groupId }, row: { key, name: names, detail: t('mobile.logger.moreTime.addRound', { defaultValue: '+1 set each' }) } }];
    }
    const entry = entryOfSlot(draft, item.slotId, item.exerciseId);
    // A row in its MAX calibration takes neither (moreTimeReducer).
    if (!entry || inCalibration(entry)) return [];
    const entryId = rowIdOf(entry);
    if (item.add === 'reps') {
      const reps = item.reps ?? 0;
      // No set left to do: moreTimeItems already left it out (setsToDo).
      if (reps <= 0) return [];
      return [{ item, step: { add: 'reps', entryId, reps }, row: { key, name, detail: t('mobile.logger.moreTime.addReps', { count: reps, defaultValue: '+{{count}} reps' }) } }];
    }
    return [{ item, step: { add: 'set', entryId }, row: { key, name, detail: t('mobile.logger.moreTime.addSet', { defaultValue: '+1 set' }) } }];
  });
};

/** Is there any step at the widest ask? Words do not open the door, so none are made. */
export const hasMoreTime = (source: MoreTimeSource, draft: LoggerDraft, max: MoreTimeMinutes, inputs: KeepInputs): boolean =>
  keptFor(source, draft, max, { ...inputs, resolveName: (slug) => slug, t: (k) => k }).length > 0;
